/* eslint-disable no-nested-ternary */
import React, { FC, useContext, useState } from 'react';
import styled from 'styled-components';
import Select, { StylesConfig } from 'react-select';
import { Card } from '@/components/card';
import { VSpace } from '@/components/spacing';
import { AssistedProduct, SummaryResponse } from '@/webapi/use-analytics-api';
import { Tooltip } from '@/components/tooltip';
import { currencySymbol } from '@/utils/currencies';
import { nFormatter } from '@/utils/experience-utils';
import { ExperienceVariant } from '@/webapi/use-experience-api';
import { maybe } from '@/features/details/utils';
import { SummaryContext } from '@/features/details/summary/context';
import {
  isProfitMetricAvailable,
  isReturnsMetricAvailable,
  MetricKind,
} from '@/webapi/models';
import { breakpoints, isMobile } from '@/components/responsive';

export const AssistedProducts: FC<{
  summaryData: SummaryResponse;
  isAudience: boolean;
}> = ({ summaryData, isAudience }) => {
  const { variants, data, metricsStartAt } = useContext(SummaryContext);
  const isProfitEnabled = isProfitMetricAvailable(metricsStartAt);
  const isReturnsEnabled = isReturnsMetricAvailable(metricsStartAt);
  const products = [
    ...(summaryData?.assistedProducts || []),
    ...profitProducts(isProfitEnabled, summaryData),
    ...returnedProducts(isReturnsEnabled, summaryData),
  ];

  const groups = groupByVariantAndType(products);

  const [filter, setFilter] = useState<string>(getDefaultFilter(groups));

  const productsList = sortedProductsByFilter(products, filter);

  const total = findTotal(productsList, isProfitEnabled, data, variants);

  const { variantNames } = getVariantNames(variants);

  const selectOptions = Array.from(groups.keys()).map((value) => ({
    value,
    label: makeLabel(value, variantNames, isAudience),
  }));

  const isMobileDevice = isMobile();

  const [isExpanded, setIsExpanded] = useState(false);

  if (!variants?.length || !filter) {
    return null;
  }

  return (
    <>
      <VSpace value={2} />
      <Container>
        <FlexRow>
          <SelectContainer>
            <Select
              className="only-desktop"
              key={`mob${filter}`}
              menuPlacement="bottom"
              styles={selectStyles}
              isDisabled={selectOptions.length === 1}
              defaultValue={{
                value: filter,
                label: makeLabel(filter, variantNames, isAudience),
              }}
              onChange={(x: any) => {
                setFilter(x.value);
              }}
              options={selectOptions}
            />
            <Select
              className="only-mobile full-width"
              key={`desk${filter}`}
              menuPlacement="bottom"
              styles={mobileSelectStyles}
              isDisabled={selectOptions.length === 1}
              defaultValue={{
                value: filter,
                label: makeLabel(filter, variantNames, isAudience),
              }}
              onChange={(x: any) => {
                setFilter(x.value);
              }}
              options={selectOptions}
            />
            {!isMobileDevice && <AssistedTooltip filter={filter} />}
          </SelectContainer>
          <Total style={{ opacity: !total ? 0 : 1 }}>
            Total:{` `}
            {productsList?.[0]?.type !== `returns` ? currencySymbol() : ``}
            {total?.toLocaleString() || ``}
          </Total>
        </FlexRow>
        <Grid className={`${isExpanded ? `expanded` : `collapsed`}`}>
          {productsList?.map((p, idx) => {
            const hasItemsCount = p.items && p.items !== `0`;
            const notReturns = p.type !== `returns`;
            return idx < 12 ? (
              <ProductTile key={idx}>
                {hasNoCost(p) ? (
                  <Revenue>Please Set Item Cost</Revenue>
                ) : (
                  <Revenue>
                    {notReturns && currencySymbol()}
                    {notReturns && formattedRevenue(p)}
                    {hasItemsCount
                      ? ` ${notReturns ? `|` : ``} ${p.items} items`
                      : ``}
                  </Revenue>
                )}
                <ImageBox>
                  <img src={p?.imageSrc} alt={p?.title} />
                </ImageBox>
                <Title>{p?.title || ``}</Title>
              </ProductTile>
            ) : null;
          })}
        </Grid>
      </Container>
      {isMobileDevice && !isExpanded && (
        <ShowMoreButton onClick={() => setIsExpanded(true)}>
          Show More
        </ShowMoreButton>
      )}
    </>
  );
};

const Container = styled(Card)`
  && {
    position: relative;
    cursor: default;
    padding: 3.5rem;
    border-radius: 2rem;

    ${breakpoints.down(`md`)} {
      width: 90%;
      margin: 0 auto;
      overflow: hidden;
      padding: 2rem;
      border-radius: 0.8rem;
    }

    .full-width {
      width: 100%;
    }
  }
`;
const Grid = styled.div`
  grid-row-gap: 2.5rem;
  grid-template-columns: repeat(6, 1fr);
  grid-column-gap: 1rem;
  display: grid;

  ${breakpoints.down(`md`)} {
    grid-template-columns: 1fr 1fr;
    grid-row-gap: 3.5rem;

    &&.collapsed {
      && > *:nth-child(n + 5) {
        display: none;
      }
    }
  }
`;

const FlexRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  ${breakpoints.down(`md`)} {
    flex-direction: column;
  }
`;
const SelectContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
  width: 350px;

  ${breakpoints.down(`md`)} {
    width: 100%;
    margin: 0;
  }
`;

export const ProductTile = styled.div`
  min-height: 140px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;
export const Title = styled.span`
  margin-top: 1rem;
  color: #798293;
  font-family: Inter, serif;
  font-weight: 500;
  font-size: 1.2rem;
  max-width: 150px;
  text-align: center;
  height: 33px;
  white-space: normal;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
`;
const ImageBox = styled.div`
  width: 140px;
  height: 140px;
  overflow: hidden;

  img {
    aspect-ratio: 16/16;
    width: 100%;

    object-fit: cover;
    object-position: left;

    border-radius: 15px;
  }
`;

const Revenue = styled.span`
  background-color: #5d8ef2;
  position: absolute;
  top: -14px;
  left: 5px;
  color: white;
  max-width: 144px;
  border-radius: 20px;
  padding: 0.5rem;
  box-shadow: rgb(0 0 0 /14%) 0 3px 0.8rem 0;
  font-family: Inter, serif;
  font-size: 1.2rem;
  font-weight: 500;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const Total = styled.span`
  padding: 1rem;
  font-family: Inter, serif;
  color: black;
  font-size: 1.6rem;
  font-weight: 600;
  height: 40px;

  ${breakpoints.down(`md`)} {
    width: 100%;
    font-size: 1.4rem;
    margin-top: 1rem;
    margin-bottom: 2rem;
    padding: 1rem 0.4rem;
  }
`;

export const ShowMoreButton = styled.button`
  outline: none;
  appearance: none;
  border: 1px solid rgba(151, 151, 151, 0.27);
  border-radius: 0.8rem;
  background: transparent;
  color: #a6afb8;
  font-size: 1.4rem;
  text-align: center;
  text-transform: uppercase;
  margin: 2rem auto;
  display: block;
  width: 80%;
  padding: 1rem 2rem;
`;

export const selectStyles: StylesConfig<any> = {
  menu: (styles) => ({
    ...styles,
  }),
  control: (styles, { isFocused, menuIsOpen, isDisabled }) => ({
    ...styles,
    border: isDisabled
      ? `none`
      : isFocused || menuIsOpen
      ? `1px solid #a1a9ac`
      : `1px solid #aaaaaa`,
    outline: `none`,
    borderRadius: `10px`,
    backgroundColor: isDisabled ? `auto` : styles?.backgroundColor,
    minWidth: `300px`,
    height: `40px`,
    boxShadow: `none`,
    ':hover': {
      border: `1px solid black`,
    },
    ':active': {
      border: `1px solid black`,
    },
  }),
  input: (styles) => ({
    ...styles,
    fontSize: `1.6rem`,
    fontFamily: `Inter, serif`,
    fontWeight: 700,
  }),
  placeholder: (styles) => ({
    ...styles,
    fontSize: `1.6rem`,
    fontFamily: `Inter, serif`,
    fontWeight: 700,
  }),
  option: (styles, { isSelected }) => ({
    ...styles,
    fontSize: `1.6rem`,
    fontWeight: 700,
    fontFamily: `Inter, serif`,
    background: isSelected ? `lightgray` : `white`,
    ':hover': {
      background: `grey`,
      color: `white`,
    },
  }),
  singleValue: (styles) => ({
    ...styles,
    color: `black`,
  }),
  indicatorsContainer: (base, { isDisabled }) => ({
    ...base,
    display: isDisabled ? `none` : `block`,
  }),
  indicatorSeparator: () => ({
    display: `none`,
  }),
  valueContainer: (styles) => ({
    ...styles,
    fontSize: `1.6rem`,
    fontFamily: `Inter, serif`,
    fontWeight: 700,
  }),
};

const mobileSelectStyles: StylesConfig<any> = {
  menuPortal: (styles, props) => ({
    ...styles,
    ...selectStyles?.menuPortal?.(styles, props),
  }),
  menu: (styles, props) => ({
    ...styles,
    ...selectStyles?.menu?.(styles, props),
  }),
  menuList: (styles, props) => ({
    ...styles,
    ...selectStyles?.menuList?.(styles, props),
  }),
  group: (styles, props) => ({
    ...styles,
    ...selectStyles?.group?.(styles, props),
  }),
  option: (styles, props) => ({
    ...styles,
    ...selectStyles?.option?.(styles, props),
    fontSize: `1.4rem`,
  }),
  indicatorSeparator: (styles, props) => ({
    ...styles,
    ...selectStyles?.indicatorSeparator?.(styles, props),
  }),
  dropdownIndicator: (styles, props) => ({
    ...styles,
    ...selectStyles?.dropdownIndicator?.(styles, props),
  }),
  indicatorsContainer: (styles, props) => ({
    ...styles,
    ...selectStyles?.indicatorsContainer?.(styles, props),
  }),
  valueContainer: (styles, props) => ({
    ...styles,
    ...selectStyles?.valueContainer?.(styles, props),
    fontSize: `1.2rem`,
  }),
  singleValue: (styles, props) => ({
    ...styles,
    ...selectStyles?.singleValue?.(styles, props),
    fontSize: `1.2rem`,
  }),
  control: (styles, props) => ({
    ...styles,
    ...selectStyles?.control?.(styles, props),
    fontSize: `1.2rem`,
    height: `36px`,
    minHeight: `36px`,
  }),
  input: (styles, props) => ({
    ...styles,
    ...selectStyles?.input?.(styles, props),
    fontSize: `1.2rem`,
  }),
  placeholder: (styles, props) => ({
    ...styles,
    ...selectStyles?.placeholder?.(styles, props),
    fontSize: `1.2rem`,
  }),
};

function hasNoCost(p: AssistedProduct) {
  return p.type === `profit` && p?.revenue === ``;
}

export function formattedRevenue(p: AssistedProduct) {
  const ret = p?.revenue || `0`;
  if (parseInt(ret, 10) > 10000) {
    return nFormatter(ret, 2);
  }
  try {
    return parseFloat(parseFloat(ret).toFixed(0)).toLocaleString();
  } catch (e) {
    return ret;
  }
}

function getDefaultFilter(groups: Map<string, Array<AssistedProduct>>) {
  const defFilter = maybe(
    () =>
      Array.from(groups.keys())
        .filter((t) => !t.includes(`profit`))
        .filter((t) => !t.includes(`return`))
        .map((id) => ({
          id,
          total: getTotal(groups.get(id)),
        }))
        ?.reduce((p, c) => (p.total > c.total ? p : c)).id,
    ``,
  );
  if (!defFilter) {
    return maybe(() => Array.from(groups.keys())[0]);
  }
  return defFilter;
}

function getTotal(productsList: AssistedProduct[]) {
  return productsList
    ?.map((x) => parseInt(x.revenue, 10))
    ?.filter((x) => x > 0)
    ?.reduce((previousValue, currentValue) => previousValue + currentValue, 0);
}

export function getVariantNames(variants: Array<ExperienceVariant>) {
  const isMultiVariant = variants?.length > 2;
  const variantNames = new Map<string, string>();
  variants?.forEach((v) => {
    variantNames.set(v.id, v.name);
  });

  return { isMultiVariant, variantNames };
}

function getKey(p: AssistedProduct) {
  return `${p.variantId}_${p.type}`;
}

export function groupByVariantAndType(productsList: AssistedProduct[]) {
  const groups = new Map<string, Array<AssistedProduct>>();
  productsList.forEach((p) => {
    if (groups.has(getKey(p))) {
      groups.get(getKey(p)).push(p);
    } else {
      groups.set(getKey(p), [p]);
    }
  });
  return groups;
}

function getVariantId(value: string) {
  return value.split(`_`)[0];
}

function getType(value: string) {
  return value.split(`_`)[1];
}

function makeLabel(
  value: string,
  variantNames: Map<string, string>,
  isAudience?: boolean,
) {
  let type = getType(value);
  type = type.charAt(0).toUpperCase() + type.slice(1);
  let name = variantNames.get(getVariantId(value)) || ``;
  if (isAudience) {
    name = ``;
  }
  if (type.toLowerCase() === `profit` || type.toLowerCase() === `returns`) {
    return `${type} ${name ? `  - ${name}` : ``}`;
  }
  return `${type} Revenue${name ? `  - ${name}` : ``}`;
}

function sortedProductsByFilter(
  products: Array<AssistedProduct>,
  filter: string,
) {
  return (
    products
      ?.filter((p) => getKey(p) === filter)
      ?.sort(
        (a, b) =>
          parseInt(b?.revenue || `0`, 10) - parseInt(a?.revenue || `0`, 10),
      ) || []
  )?.slice(0, 12);
}

function getTotalForPredicate(
  data: SummaryResponse,
  productsList: AssistedProduct[],
  variants: Array<ExperienceVariant>,
  predicate: (m) => boolean,
) {
  return (
    data?.variants
      ?.filter((v) => v.variantId === productsList?.[0]?.variantId)
      ?.map((x) => ({
        variantName: variants.find((v) => v.id === x.variantId)?.name,
        variantId: x.variantId,
        profit: x.metrics?.find(predicate)?.value,
      }))?.[0]?.profit || 0
  );
}

function findTotal(
  productsList: AssistedProduct[],
  isEnabled: boolean,
  data: SummaryResponse,
  variants: Array<ExperienceVariant>,
) {
  let total = getTotal(productsList);
  if (productsList?.find((p) => p.type === `assisted`)?.type) {
    total = getTotalForPredicate(
      data,
      productsList,
      variants,
      (m) => m.name === MetricKind.ASSISTED_REVENUE,
    );
  }
  if (productsList?.find((p) => p.type === `direct`)?.type) {
    total = getTotalForPredicate(
      data,
      productsList,
      variants,
      (m) => m.name === MetricKind.DIRECT_REVENUE,
    );
  }
  if (isEnabled && !!productsList?.find((p) => p.type === `returns`)?.type) {
    total = getTotalForPredicate(
      data,
      productsList,
      variants,
      (m) => m.name === MetricKind.RETURNS,
    );
  }
  if (isEnabled && !!productsList?.find((p) => p.type === `profit`)?.type) {
    total = getTotalForPredicate(
      data,
      productsList,
      variants,
      (m) => m.name === MetricKind.PROFIT,
    );
  }
  return total;
}

function profitProducts(
  isProfitEnabled: boolean,
  summaryData: SummaryResponse,
) {
  return ((isProfitEnabled ? summaryData?.topProducts : []) || []).map(
    (tp) =>
      ({
        ...tp,
        items: `${tp.items}`,
        type: `profit`,
        revenue: `${tp.profit || ``}`,
      } as AssistedProduct),
  );
}

function returnedProducts(
  isReturnsEnabled: boolean,
  summaryData: SummaryResponse,
) {
  return ((isReturnsEnabled ? summaryData?.returns : []) || []).map(
    (tp) =>
      ({
        ...tp,
        items: `${tp.returns}`,
        type: `returns`,
        revenue: `0`,
      } as AssistedProduct),
  );
}

function AssistedTooltip({ filter }: { filter: string }) {
  const title = filter.includes(`assisted`)
    ? `Assisted revenue`
    : filter.includes(`profit`)
    ? `Profit`
    : filter.includes(`return`)
    ? `Returns`
    : `Direct revenue`;

  const text = filter.includes(`assisted`)
    ? `Revenue generated from products bought after clicking on a recommended item and buying any product within the session.`
    : filter.includes(`profit`)
    ? `Profit is calculated based on the variant cost defined in Shopify, profit = quantity * ( price - cost ) - discount. If a variant has no defined cost it is ignored.`
    : filter.includes(`return`)
    ? `Refunded order line items`
    : `Revenue generated from products bought after clicking on an item and buying that item (or items from its group ID) within a 30 day window.`;

  return <Tooltip key={title} title={title} text={text} />;
}
